import React, { useState, useMemo, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import { supabase } from '../../supabaseClient';
import logoImage from '../../logo1.png';
import { embedSpaceGroteskFont } from '../../utils/fontUtils';
import TrainerBrandingSettings from '../settings/TrainerBrandingSettings';

interface DataExportSystemProps {
  selectedUserId: string;
  clientName: string;
  clientData: any[];
  calculatedGoals?: {
    targetCalories: number;
    protein: { min: number; max: number; target: number };
    carbs: { min: number; max: number; target: number };
    fat: { min: number; max: number; target: number };
  } | null;
}

interface ReportData {
  clientName: string;
  reportDate: string;
  timeRange: string;
  totalMeals: number;
  averageCalories: number;
  averageProtein: number;
  averageCarbs: number;
  averageFat: number;
  goalAchievement: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  consistency: number;
  topFoods: Array<{ name: string; frequency: number }>;
  dailyBreakdown: Array<{
    date: string;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    meals: number;
  }>;
}

export default function DataExportSystem({ 
  selectedUserId, 
  clientName, 
  clientData, 
  calculatedGoals 
}: DataExportSystemProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportType, setReportType] = useState<'pdf' | 'csv'>('pdf');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [trainerBranding, setTrainerBranding] = useState<{
    business_name: string | null;
    logo_url: string | null;
  } | null>(null);

  // Fetch trainer branding
  useEffect(() => {
    const fetchTrainerBranding = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .rpc('get_trainer_branding', { p_trainer_id: user.id });

        if (error) {
          console.error('Error fetching trainer branding:', error);
        } else if (data && data.length > 0) {
          setTrainerBranding(data[0]);
        }
      } catch (error) {
        console.error('Error fetching trainer branding:', error);
      }
    };

    fetchTrainerBranding();
  }, []);

  // Process data for reporting
  const reportData = useMemo((): ReportData => {
    if (!clientData.length) {
      return {
        clientName,
        reportDate: new Date().toLocaleDateString(),
        timeRange,
        totalMeals: 0,
        averageCalories: 0,
        averageProtein: 0,
        averageCarbs: 0,
        averageFat: 0,
        goalAchievement: { calories: 0, protein: 0, carbs: 0, fat: 0 },
        consistency: 0,
        topFoods: [],
        dailyBreakdown: []
      };
    }

    const now = new Date();
    const daysToShow = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(now.getTime() - (daysToShow - 1) * 24 * 60 * 60 * 1000);

    // Filter data for selected time range
    const filteredData = clientData.filter(meal => 
      new Date(meal.meal_time) >= startDate
    );

    // Calculate averages
    const totalCalories = filteredData.reduce((sum, meal) => sum + (meal.calories || 0), 0);
    const totalProtein = filteredData.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
    const totalCarbs = filteredData.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
    const totalFat = filteredData.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

    const averageCalories = totalCalories / daysToShow;
    const averageProtein = totalProtein / daysToShow;
    const averageCarbs = totalCarbs / daysToShow;
    const averageFat = totalFat / daysToShow;

    // Calculate goal achievement percentages
    const goalAchievement = {
      calories: calculatedGoals ? (averageCalories / calculatedGoals.targetCalories) * 100 : 0,
      protein: calculatedGoals ? (averageProtein / calculatedGoals.protein.target) * 100 : 0,
      carbs: calculatedGoals ? (averageCarbs / calculatedGoals.carbs.target) * 100 : 0,
      fat: calculatedGoals ? (averageFat / calculatedGoals.fat.target) * 100 : 0
    };

    // Calculate consistency (days with meals logged)
    const daysWithMeals = new Set(
      filteredData.map(meal => new Date(meal.meal_time).toISOString().slice(0, 10))
    ).size;
    const consistency = (daysWithMeals / daysToShow) * 100;

    // Get top foods
    const foodFrequency: { [key: string]: number } = {};
    filteredData.forEach(meal => {
      const foodName = meal.input_text || 'Unknown Food';
      foodFrequency[foodName] = (foodFrequency[foodName] || 0) + 1;
    });
    const topFoods = Object.entries(foodFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([name, frequency]) => ({ name, frequency }));

    // Create daily breakdown
    const dailyBreakdown = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      const dateKey = date.toISOString().slice(0, 10);
      const dayMeals = filteredData.filter(meal => 
        new Date(meal.meal_time).toISOString().slice(0, 10) === dateKey
      );

      dailyBreakdown.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        calories: dayMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0),
        protein: dayMeals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0),
        carbs: dayMeals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0),
        fat: dayMeals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0),
        meals: dayMeals.length
      });
    }

    return {
      clientName,
      reportDate: new Date().toLocaleDateString(),
      timeRange,
      totalMeals: filteredData.length,
      averageCalories,
      averageProtein,
      averageCarbs,
      averageFat,
      goalAchievement,
      consistency,
      topFoods,
      dailyBreakdown
    };
  }, [clientData, clientName, timeRange, calculatedGoals]);



  // Utility function to embed logo with better error handling
  const embedLogo = async (doc: jsPDF, x: number, y: number, width: number, height: number): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        // Set crossOrigin to handle CORS issues
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            
            // Convert to base64 with proper format
            const logoDataUrl = canvas.toDataURL('image/png', 1.0);
            
            // Add logo to PDF with proper positioning
            doc.addImage(logoDataUrl, 'PNG', x, y, width, height);
            
            console.log('Logo successfully added to PDF');
            resolve(true);
          } catch (canvasError) {
            console.error('Canvas conversion error:', canvasError);
            resolve(false);
          }
        };
        
        img.onerror = (error) => {
          console.error('Image loading error:', error);
          resolve(false);
        };
        
        // Set the image source
        img.src = logoImage;
        
        // Timeout fallback
        setTimeout(() => {
          if (!img.complete) {
            console.log('Image loading timeout, using fallback');
            resolve(false);
          }
        }, 2000);
        
      } catch (error) {
        console.error('Logo embedding error:', error);
        resolve(false);
      }
    });
  };

  // Utility function to embed custom logo from URL
  const embedCustomLogo = async (doc: jsPDF, logoUrl: string, x: number, y: number, width: number, height: number): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        // Set crossOrigin to handle CORS issues
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            
            // Convert to base64 with proper format
            const logoDataUrl = canvas.toDataURL('image/png', 1.0);
            
            // Add logo to PDF with proper positioning
            doc.addImage(logoDataUrl, 'PNG', x, y, width, height);
            
            console.log('Custom logo successfully added to PDF');
            resolve(true);
          } catch (canvasError) {
            console.error('Canvas conversion error for custom logo:', canvasError);
            resolve(false);
          }
        };
        
        img.onerror = (error) => {
          console.error('Custom logo loading error:', error);
          resolve(false);
        };
        
        // Set the image source
        img.src = logoUrl;
        
        // Timeout fallback
        setTimeout(() => {
          if (!img.complete) {
            console.log('Custom logo loading timeout, using fallback');
            resolve(false);
          }
        }, 2000);
        
      } catch (error) {
        console.error('Custom logo embedding error:', error);
        resolve(false);
      }
    });
  };

  // Utility function to add page header with logo
  const addPageHeader = async (doc: jsPDF, title: string) => {
    // Set cream background for the entire page
    doc.setFillColor(248, 244, 230); // #f8f4e6 - MacTrack paper-cream
    doc.rect(0, 0, 210, 297, 'F');
    
    // Create white content area
    doc.setFillColor(255, 255, 255); // White background for content
    doc.rect(10, 10, 190, 277, 'F');
    
    // Add subtle border
    doc.setDrawColor(212, 212, 212); // #d4d4d4 - MacTrack border-light
    doc.setLineWidth(1);
    doc.rect(10, 10, 190, 277, 'S');
    
    // Use custom branding if available, otherwise fall back to MacTrack
    const businessName = trainerBranding?.business_name || 'MacTrack';
    const customLogoUrl = trainerBranding?.logo_url;
    
    // Try to embed custom logo first, then fall back to MacTrack logo
    let logoSuccess = false;
    if (customLogoUrl) {
      logoSuccess = await embedCustomLogo(doc, customLogoUrl, 20, 20, 25, 25);
    }
    
    if (!logoSuccess) {
      // Fall back to MacTrack logo
      logoSuccess = await embedLogo(doc, 20, 20, 25, 25);
    }
    
    if (!logoSuccess) {
      // Final fallback: Add a styled text-based logo
      doc.setTextColor(31, 31, 31);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('MM', 20, 32); // Simple "MM" logo
    }
    
    // Main business name text (positioned next to logo)
    doc.setTextColor(31, 31, 31); // #1f1f1f - MacTrack text-dark
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(businessName, 50, 35);
    
    // Page title
    doc.setTextColor(31, 31, 31); // #1f1f1f - MacTrack text-dark
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 20, 65);
  };

  const generatePDFReport = async () => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      
             // Embed Space Grotesk font (or fallback)
       await embedSpaceGroteskFont(doc);
      
      // Add first page header
      await addPageHeader(doc, 'Client Progress Report');
      
      // Client info section with proper MacTrack styling
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Client Information', 20, 80);
       
      // Add white background panel for client info (like the UI)
      doc.setFillColor(255, 255, 255); // White background
      doc.rect(15, 85, 180, 40, 'F');
       
      // Add subtle border
      doc.setDrawColor(212, 212, 212); // #d4d4d4 - MacTrack border-light
      doc.setLineWidth(1);
      doc.rect(15, 85, 180, 40, 'S');
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Client: ${reportData.clientName}`, 20, 95);
      doc.text(`Report Date: ${reportData.reportDate}`, 20, 105);
      doc.text(`Time Range: ${timeRange === '7d' ? '7 Days' : timeRange === '30d' ? '30 Days' : '90 Days'}`, 20, 115);

      // Summary section with proper MacTrack styling
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', 20, 140);
      
      // Add white background panel for summary (like the UI)
      doc.setFillColor(255, 255, 255); // White background
      doc.rect(15, 145, 180, 30, 'F');
      
      // Add subtle border
      doc.setDrawColor(212, 212, 212); // #d4d4d4 - MacTrack border-light
      doc.setLineWidth(1);
      doc.rect(15, 145, 180, 30, 'S');
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Meals Logged: ${reportData.totalMeals}`, 20, 155);
      doc.text(`Consistency: ${reportData.consistency.toFixed(1)}%`, 20, 165);

      // Performance Metrics section
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance Metrics', 20, 190);
      
      // Add white background panel for metrics table
      doc.setFillColor(255, 255, 255); // White background
      doc.rect(15, 195, 180, 60, 'F');
      
      // Add subtle border
      doc.setDrawColor(212, 212, 212); // #d4d4d4 - MacTrack border-light
      doc.setLineWidth(1);
      doc.rect(15, 195, 180, 60, 'S');

      const averagesData = [
         ['Metric', 'Average', 'Target', 'Achievement'],
         ['Calories', `${reportData.averageCalories.toFixed(0)}`, 
          calculatedGoals ? `${calculatedGoals.targetCalories}` : 'N/A',
          `${reportData.goalAchievement.calories.toFixed(1)}%`],
         ['Protein (g)', `${reportData.averageProtein.toFixed(1)}`, 
          calculatedGoals ? `${calculatedGoals.protein.target}` : 'N/A',
          `${reportData.goalAchievement.protein.toFixed(1)}%`],
         ['Carbs (g)', `${reportData.averageCarbs.toFixed(1)}`, 
          calculatedGoals ? `${calculatedGoals.carbs.target}` : 'N/A',
          `${reportData.goalAchievement.carbs.toFixed(1)}%`],
         ['Fat (g)', `${reportData.averageFat.toFixed(1)}`, 
          calculatedGoals ? `${calculatedGoals.fat.target}` : 'N/A',
          `${reportData.goalAchievement.fat.toFixed(1)}%`]
       ];

             autoTable(doc, {
          startY: 200,
          head: [averagesData[0]],
          body: averagesData.slice(1),
          theme: 'grid',
          headStyles: { 
            fillColor: [248, 244, 230], // MacTrack paper-cream (like analytics section)
            textColor: [31, 31, 31], // Dark text
            fontStyle: 'bold',
            fontSize: 11
          },
          bodyStyles: {
            textColor: [31, 31, 31], // MacTrack text-dark
            fontSize: 10
          },
          alternateRowStyles: {
            fillColor: [255, 255, 255] // White alternating rows
          },
          margin: { top: 5, right: 20, bottom: 5, left: 20 },
          tableWidth: 170 // Constrain table width to fit within the white content area
        });

      // Top foods table with proper MacTrack styling
      if (reportData.topFoods.length > 0) {
        doc.addPage();
        
        // Add page header with logo
        await addPageHeader(doc, 'Most Frequently Logged Foods');
        
        const foodsData = reportData.topFoods.map(food => 
          [food.name, food.frequency.toString()]
        );

                 autoTable(doc, {
            startY: 75,
            head: [['Food Name', 'Times Logged']],
            body: foodsData,
            theme: 'grid',
            headStyles: { 
              fillColor: [248, 244, 230], // MacTrack paper-cream (like analytics section)
              textColor: [31, 31, 31], // Dark text
              fontStyle: 'bold',
              fontSize: 11
            },
            bodyStyles: {
              textColor: [31, 31, 31], // MacTrack text-dark
              fontSize: 10
            },
            alternateRowStyles: {
              fillColor: [255, 255, 255] // White alternating rows
            },
            margin: { top: 5, right: 20, bottom: 5, left: 20 },
            tableWidth: 170 // Constrain table width to fit within the white content area
          });
      }

      // Daily breakdown table with proper MacTrack styling
      if (reportData.dailyBreakdown.length > 0) {
        doc.addPage();
        
        // Add page header with logo
        await addPageHeader(doc, 'Daily Breakdown');
        
        const breakdownData = reportData.dailyBreakdown.map(day => [
          day.date,
          day.calories.toFixed(0),
          day.protein.toFixed(1),
          day.carbs.toFixed(1),
          day.fat.toFixed(1),
          day.meals.toString()
        ]);

                 autoTable(doc, {
            startY: 75,
            head: [['Date', 'Calories', 'Protein (g)', 'Carbs (g)', 'Fat (g)', 'Meals']],
            body: breakdownData,
            theme: 'grid',
            headStyles: { 
              fillColor: [248, 244, 230], // MacTrack paper-cream (like analytics section)
              textColor: [31, 31, 31], // Dark text
              fontStyle: 'bold',
              fontSize: 10
            },
            bodyStyles: {
              textColor: [31, 31, 31], // MacTrack text-dark
              fontSize: 9
            },
            alternateRowStyles: {
              fillColor: [255, 255, 255] // White alternating rows
            },
            margin: { top: 5, right: 20, bottom: 5, left: 20 },
            tableWidth: 170 // Constrain table width to fit within the white content area
          });
      }

      // Save the PDF
      doc.save(`${clientName}_Progress_Report_${timeRange}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCSVExport = async () => {
    setIsGenerating(true);
    try {
      // Create CSV data
      const csvData = clientData.map(meal => ({
        Date: new Date(meal.meal_time).toLocaleDateString(),
        Time: new Date(meal.meal_time).toLocaleTimeString(),
        'Food Name': meal.input_text || 'Unknown',
        Calories: meal.calories || 0,
        'Protein (g)': meal.protein_g || 0,
        'Carbs (g)': meal.carbs_g || 0,
        'Fat (g)': meal.fat_g || 0,
        'Fiber (g)': meal.fiber_g || 0,
        'Sugar (g)': meal.sugar_g || 0,
        'Sodium (mg)': meal.sodium_mg || 0
      }));

      // Convert to CSV
      const csv = Papa.unparse(csvData);
      
      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${clientName}_Meal_Data_${timeRange}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV export. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = async () => {
    if (reportType === 'pdf') {
      await generatePDFReport();
    } else {
      await generateCSVExport();
    }
  };

  return (
    <div className="retro-card">
      <div className="index-tab">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">
            📊 Data Export & Reporting
          </h3>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6">
        {/* Branding Settings */}
        <TrainerBrandingSettings />

        {/* Report Type Selection */}
        <div className="flex justify-center">
          <div className="flex space-x-2 bg-white rounded-lg p-1 shadow">
            <button
              onClick={() => setReportType('pdf')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                reportType === 'pdf'
                  ? 'bg-[#0eeaff] text-[#1f1f1f] shadow'
                  : 'text-[#2d2d2d] hover:bg-[#f8f4e6]'
              }`}
            >
              📄 PDF Report
            </button>
            <button
              onClick={() => setReportType('csv')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                reportType === 'csv'
                  ? 'bg-[#0eeaff] text-[#1f1f1f] shadow'
                  : 'text-[#2d2d2d] hover:bg-[#f8f4e6]'
              }`}
            >
              📋 CSV Export
            </button>
          </div>
        </div>

        {/* Time Range Selection */}
        <div className="flex justify-center">
          <div className="flex space-x-2 bg-white rounded-lg p-1 shadow">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-[#0eeaff] text-[#1f1f1f] shadow'
                    : 'text-[#2d2d2d] hover:bg-[#f8f4e6]'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>

        {/* Report Preview */}
        {reportType === 'pdf' && (
          <div className="bg-gradient-to-r from-[#0eeaff]/10 to-[#8aff8a]/10 rounded-lg p-4 border border-[#d4d4d4]">
            <h4 className="font-semibold text-[#1f1f1f] mb-3 flex items-center">
              <span className="text-lg mr-2">👁️</span>
              Report Preview
            </h4>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-[#2d2d2d]">👤 Client:</span>
                <span className="text-[#1f1f1f]">{reportData.clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[#2d2d2d]">📅 Time Range:</span>
                <span className="text-[#1f1f1f]">{timeRange === '7d' ? '7 Days' : timeRange === '30d' ? '30 Days' : '90 Days'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[#2d2d2d]">🍽️ Total Meals:</span>
                <span className="text-[#1f1f1f]">{reportData.totalMeals}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[#2d2d2d]">📈 Consistency:</span>
                <span className="text-[#1f1f1f]">{reportData.consistency.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[#2d2d2d]">🔥 Avg Calories:</span>
                <span className="text-[#1f1f1f]">{reportData.averageCalories.toFixed(0)}</span>
              </div>
              {calculatedGoals && (
                <div className="flex justify-between">
                  <span className="font-medium text-[#2d2d2d]">🎯 Goal Achievement:</span>
                  <span className="text-[#1f1f1f]">{reportData.goalAchievement.calories.toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Export Button */}
        <div className="flex justify-center">
          <button
            onClick={handleExport}
            disabled={isGenerating || clientData.length === 0}
            className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
              isGenerating || clientData.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#1f1f1f] text-white hover:bg-[#0eeaff] hover:text-[#1f1f1f] border border-[#0eeaff] hover:shadow-lg transform hover:-translate-y-0.5'
            }`}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating {reportType.toUpperCase()}...
              </span>
            ) : (
              <span className="flex items-center">
                <span className="mr-2">📤</span>
                Generate {reportType.toUpperCase()} Report
              </span>
            )}
          </button>
        </div>

        {clientData.length === 0 && (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-sm text-gray-500">
              No data available for export. Please ensure the client has logged meals.
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 